import React, { useEffect, useState } from "react";
import Icon from "../AppIcon";
import { base_url } from "Backend/api";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Lottie from "lottie-react";

const SwipeToDeleteNotificationItem = ({
  children,
  onDelete,
  onClick,
  runDemo = false,
  onDemoFinish,
  disabled = false,
}) => {
  const [offsetX, setOffsetX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [playedDemo, setPlayedDemo] = useState(false);

  const MAX_REVEAL = 86;
  const OPEN_THRESHOLD = 34;

  const getX = (event) => {
    if (event.touches?.length) return event.touches[0].clientX;
    if (event.changedTouches?.length) return event.changedTouches[0].clientX;
    return event.clientX;
  };

  useEffect(() => {
    if (!runDemo || playedDemo) return undefined;
    setPlayedDemo(true);
    const t1 = setTimeout(() => setOffsetX(-58), 250);
    const t2 = setTimeout(() => setOffsetX(0), 1050);
    const t3 = setTimeout(() => {
      if (typeof onDemoFinish === "function") onDemoFinish();
    }, 1300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [runDemo, playedDemo, onDemoFinish]);

  const onStart = (event) => {
    if (disabled) return;
    setIsDragging(true);
    setStartX(getX(event));
  };

  const onMove = (event) => {
    if (!isDragging || disabled) return;
    const delta = getX(event) - startX;
    setOffsetX(Math.max(-MAX_REVEAL, Math.min(0, delta)));
  };

  const onEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    setOffsetX((prev) => (Math.abs(prev) >= OPEN_THRESHOLD ? -MAX_REVEAL : 0));
  };

  return (
    <div className="relative">
      <div className="absolute inset-y-0 right-0 w-[86px] bg-[#F93209] flex items-center justify-center">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
            setOffsetX(0);
          }}
          className="flex flex-col items-center gap-1 text-white"
        >
          <Icon name="Trash2" size={18} />
          <span className="text-xs font-[cairo] font-semibold">حذف</span>
        </button>
      </div>

      <div
        onTouchStart={onStart}
        onTouchMove={onMove}
        onTouchEnd={onEnd}
        onMouseDown={onStart}
        onMouseMove={(e) => {
          if (isDragging) onMove(e);
        }}
        onMouseUp={onEnd}
        onMouseLeave={onEnd}
        onClick={() => {
          if (offsetX !== 0) {
            setOffsetX(0);
            return;
          }
          onClick();
        }}
        className="relative bg-white transition-transform duration-150"
        style={{ transform: `translateX(${offsetX}px)` }}
      >
        {children}
      </div>
    </div>
  );
};

const NotificationsSidebar = ({ isOpen, onClose, setUnreadCount }) => {

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [shouldRunSwipeDemo, setShouldRunSwipeDemo] = useState(false);
  const [demoTargetId, setDemoTargetId] = useState(null);
  const [deleteAnimationData, setDeleteAnimationData] = useState(null);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${base_url}/api/notifications`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      const data = await response.json();

      if (data.status) {

        const list = data?.data?.data || [];

        setNotifications(list);

        const unread = list.filter(n => !n.read_at).length;

        setUnreadCount(unread);
      }

    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      fetchNotifications();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const seen = localStorage.getItem("notifications_swipe_hint_seen");
    if (!seen) {
      setShouldRunSwipeDemo(true);
    }
  }, [isOpen]);

  useEffect(() => {
    let mounted = true;
    const loadAnimation = async () => {
      try {
        const response = await fetch("/assets/lottie/DeleteNotifi.json");
        const json = await response.json();
        if (mounted) setDeleteAnimationData(json);
      } catch (error) {
        console.error("Failed to load notification lottie:", error);
      }
    };
    loadAnimation();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!shouldRunSwipeDemo || !notifications.length) return;
    setDemoTargetId(notifications[0].id);
  }, [shouldRunSwipeDemo, notifications]);

  const handleSwipeDemoFinish = () => {
    setShouldRunSwipeDemo(false);
    setDemoTargetId(null);
    localStorage.setItem("notifications_swipe_hint_seen", "1");
  };

  // const markAsRead = async (id) => {
  //   try {

  //     const token = localStorage.getItem("token");

  //     const response = await fetch(`${base_url}/api/notifications/${id}`, {
  //       method: "PATCH",
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //         Accept: "application/json",
  //         "Content-Type": "application/json"
  //       },
  //       body: JSON.stringify({
  //         is_read: true
  //       })
  //     });

  //     if (!response.ok) {
  //       throw new Error("فشل تعليم الإشعار كمقروء");
  //     }

  //     setNotifications(prev =>
  //       prev.map(n =>
  //         n.id === id ? { ...n, read_at: new Date().toISOString() } : n
  //       )
  //     );

  //     setUnreadCount(prev => {
  //       const notification = notifications.find(n => n.id === id);
  //       if (!notification?.read_at) {
  //         return Math.max(prev - 1, 0);
  //       }
  //       return prev;
  //     });

  //     setTimeout(() => {
  //       navigate("/orders");
  //       onClose();
  //     }, 200);

  //   } catch (error) {
  //     console.error(error);
  //   }
  // };
  const markAsRead = async (notification) => {
  try {
    const token = localStorage.getItem("token");

    await fetch(`${base_url}/api/notifications/${notification.id}`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ is_read: true })
    });

    // تحديث UI
    setNotifications(prev =>
      prev.map(n =>
        n.id === notification.id
          ? { ...n, read_at: new Date().toISOString() }
          : n
      )
    );

    // 👇 هنا بقا الذكاء
    const type = notification.data?.type;

    if (type === "new_coupon") {
      navigate("/cart"); // يروح للسلة
    } else if (type === "order_created") {
      navigate(`/orders?highlight=${notification.order_id}`);
    }

    onClose();

  } catch (error) {
    console.error(error);
  }
};

  const deleteNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("يجب تسجيل الدخول أولاً");
        return;
      }

      const response = await fetch(`${base_url}/api/notifications/${notificationId}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok || response.status === 204) {
        toast.success("تم حذف الإشعار بنجاح");
        setNotifications((prev) => {
          const next = prev.filter((item) => item.id !== notificationId);
          setUnreadCount(next.filter((n) => !n.read_at).length);
          return next;
        });
      } else {
        toast.error("فشل حذف الإشعار");
      }
    } catch (error) {
      console.error(error);
      toast.error("فشل حذف الإشعار");
    }
  };

  const deleteAllNotifications = async () => {
    try {
      setDeletingAll(true);
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("يجب تسجيل الدخول أولاً");
        return;
      }

      const response = await fetch(`${base_url}/api/notifications`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok || response.status === 204) {
        toast.success("تم حذف سجل الأشعارات بنجاح");
        setNotifications([]);
        setUnreadCount(0);
        setConfirmDeleteAll(false);
      } else {
        toast.error("فشل حذف سجل الأشعارات");
      }
    } catch (error) {
      console.error(error);
      toast.error("فشل حذف سجل الأشعارات");
    } finally {
      setDeletingAll(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50  justify-start scrollbar-hide">

      {/* overlay */}
      <div
        className="flex-1 bg-black/30"
        onClick={onClose}
      />

      {/* sidebar */}
      <div className="w-[380px] max-w-[85%] bg-white h-full shadow-xl animate-slideIn">

        {/* header */}
        <div className="flex justify-between items-center p-4 border-b" dir="ltr">
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-[#F3F4F6] flex items-center justify-center"
            >
              <Icon name="X" size={16} />
            </button>
            {notifications.length > 0 && (
              <button
                type="button"
                onClick={() => setConfirmDeleteAll(true)}
                className="w-8 h-8 rounded-full bg-[#F3F4F6] text-[#F93209] flex items-center justify-center"
              >
                <Icon name="Trash2" size={16} />
              </button>
            )}
          </div>
          <h2 className="font-bold font-[cairo] text-[16px]">
            قائمة الاشعارات
          </h2>

        </div>

        {/* body */}

        {loading && (
          <div className="flex justify-center mt-10">
            <div className="loader"></div>
          </div>
        )}

        {!loading && notifications.length === 0 && (
          <p className="text-center mt-10 text-gray-500 font-[cairo]">
            لا توجد إشعارات
          </p>
        )}

        <div className="overflow-y-auto h-[calc(100%-72px)]">

          {notifications.map((n) => (

            <div key={n.id}>
              <SwipeToDeleteNotificationItem
                onDelete={() => deleteNotification(n.id)}
                onClick={() => markAsRead(n)}
                runDemo={shouldRunSwipeDemo && demoTargetId === n.id}
                onDemoFinish={handleSwipeDemoFinish}
                disabled={deletingAll}
              >
                <div
                  className={`p-4 cursor-pointer flex gap-3 transition ${n.read_at ? "bg-white" : "bg-orange-50"}`}
                  dir="rtl"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-100">
                    <Icon name="Bell" size={18} color="red" />
                  </div>

                  <div className="flex-1 text-right">
                    <p className="font-semibold text-sm font-[cairo]">
                      {n.title}
                    </p>
                    <p className="text-gray-600 text-xs font-[cairo]">
                      {n.body}
                    </p>
                  </div>

                  <span className="text-gray-400 text-xs whitespace-nowrap self-start">
                    {n.time_ago}
                  </span>
                </div>
              </SwipeToDeleteNotificationItem>

              <div className="border-b mx-6"></div>

            </div>

          ))}

        </div>

      </div>

      {confirmDeleteAll && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[60] px-4 font-[cairo]">
          <div className="w-full max-w-md rounded-[8px] bg-white p-5 text-center shadow-xl">
            <button
              type="button"
              onClick={() => setConfirmDeleteAll(false)}
              className="ml-auto mb-2 block text-gray-500"
            >
              <Icon name="X" size={18} />
            </button>
            <div className="mx-auto rounded-[6px] mb-3 flex items-center justify-center">
              {deleteAnimationData ? (
                <Lottie animationData={deleteAnimationData} loop className="w-20 h-20" />
              ) : (
                <img src="/assets/images/delOrder.svg" alt="trash" className="w-30 h-30" />
              )}
            </div>
            <h3 className="text-[16px] font-medium text-[#222] leading-[1.2] font-[cairo]">سيتم حذف كافة الإشعارات</h3>
            <p className="mt-2 text-[14px] text-[#6b7280] leading-[1.3]">هل أنت متأكد من حذف كافة السجل ؟</p>
            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                onClick={deleteAllNotifications}
                disabled={deletingAll}
                className="flex-1 rounded-[6px] bg-[#F93209] py-1.5 px-1 text-white font-semibold disabled:opacity-70"
              >
                {deletingAll ? "جارِ الحذف..." : "حذف السجل"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDeleteAll(false)}
                disabled={deletingAll}
                className="flex-1 rounded-[6px] border border-[#F93209] py-1 px-1 text-[#F93209] font-semibold"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsSidebar;